"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const benefits = [{
  title: "Vetted Local Pros",
  description: "Independent contractors are vetted before joining our network, giving you access to experienced technicians for your Atlanta home.",
  caption: "Vetted independent technicians",
  icon: <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" className="eco-icon h-12 w-12" aria-hidden="true">
        <path d="M24 6l14 6v10c0 8-6 14-14 18-8-4-14-10-14-18V12l14-6Z" />
        <path d="M18 22l4 4 8-8" />
      </svg>
}, {
  title: "Pro Quotes Before Work",
  description: "Each independent pro assesses the appliance issue and gives their quote directly before work starts, so costs remain clear.",
  caption: "Direct pro pricing with no guesswork",
  icon: <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" className="eco-icon h-12 w-12" aria-hidden="true">
        <circle cx="24" cy="24" r="16" />
        <path d="M24 16v16" />
        <path d="M18 20h12" />
        <path d="M18 28h12" />
      </svg>
}, {
  title: "Local Support",
  description: "Our Atlanta support team assists with communication and coordination from your initial match through to service completion.",
  caption: "Responsive assistance for homeowners",
  icon: <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" className="eco-icon h-12 w-12" aria-hidden="true">
        <path d="M10 34V14a4 4 0 0 1 4-4h20a4 4 0 0 1 4 4v14a4 4 0 0 1-4 4H18l-8 8Z" />
        <path d="M18 18h12" />
        <path d="M18 24h8" />
      </svg>
}];
export default function Whychoosehandyheroes() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      normal: "(prefers-reduced-motion: no-preference)"
    }, ctx => {
      const {
        reduced
      } = ctx.conditions as {
        reduced: boolean;
      };
      if (reduced) {
        gsap.set([".eco-eyebrow", ".eco-heading", ".eco-lede", ".eco-cell", ".eco-cta"], {
          opacity: 1,
          y: 0
        });
        return;
      }
      const introTl = gsap.timeline({
        defaults: {
          ease: "cubic-bezier(0.22, 1, 0.36, 1)",
          duration: 0.52
        },
        scrollTrigger: {
          trigger: container.current,
          start: "top 85%"
        }
      });
      introTl.from(".eco-eyebrow", {
        opacity: 0,
        y: 16
      }).from(".eco-heading", {
        opacity: 0,
        y: 16
      }, "+=0.12").from(".eco-lede", {
        opacity: 0,
        y: 16
      }, "+=0.12");
      gsap.from(".eco-cell", {
        opacity: 0,
        y: 16,
        stagger: 0.14,
        duration: 0.52,
        ease: "cubic-bezier(0.22, 1, 0.36, 1)",
        scrollTrigger: {
          trigger: ".eco-grid",
          start: "top 82%"
        }
      });
      const icons = gsap.utils.toArray<SVGPathElement>(".eco-icon path, .eco-icon rect, .eco-icon circle");
      icons.forEach(el => {
        const length = (el as SVGPathElement).getTotalLength ? (el as SVGPathElement).getTotalLength() : 200;
        gsap.set(el, {
          strokeDasharray: length,
          strokeDashoffset: length
        });
      });
      gsap.to(".eco-icon path, .eco-icon rect, .eco-icon circle", {
        strokeDashoffset: 0,
        duration: 0.9,
        ease: "power2.out",
        stagger: {
          each: 0.03,
          from: "start"
        },
        scrollTrigger: {
          trigger: ".eco-grid",
          start: "top 78%"
        }
      });
      gsap.from(".eco-cta", {
        opacity: 0,
        y: 12,
        duration: 0.52,
        ease: "cubic-bezier(0.22, 1, 0.36, 1)",
        scrollTrigger: {
          trigger: ".eco-cta",
          start: "top 92%"
        }
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="24729" id="whychoosehandyheroes" data-nav="light" ref={container} className="bg-background py-28 md:py-40 text-foreground">
      <div className="mx-auto w-full" style={{
      maxWidth: "1360px",
      paddingLeft: "clamp(20px, 4vw, 56px)",
      paddingRight: "clamp(20px, 4vw, 56px)"
    }}>
        <div className="mx-auto max-w-[720px] text-center">
          <div className="eco-eyebrow flex items-center justify-center gap-3">
            <span aria-hidden="true" className="inline-block" style={{
            width: "1px",
            height: "10px",
            backgroundColor: "var(--primary)"
          }} />
            <span className="font-sans font-semibold uppercase" style={{
            color: "var(--primary)",
            fontSize: "clamp(0.75rem, 0.85vw, 0.8125rem)",
            letterSpacing: "0.22em",
            lineHeight: 1
          }}>
              Why Choose Us
            </span>
            <span aria-hidden="true" className="inline-block" style={{
            width: "1px",
            height: "10px",
            backgroundColor: "var(--primary)"
          }} />
          </div>

          <h2 className="eco-heading font-serif font-bold uppercase mt-8" style={{
          color: "var(--foreground)",
          fontSize: "clamp(2.5rem, 5.2vw, 5.25rem)",
          letterSpacing: "-0.005em",
          lineHeight: 0.95
        }}>
            Vetted pros. Clear quotes. Local service.
          </h2>

          <p className="eco-lede font-sans mt-8" style={{
          color: "var(--muted-foreground)",
          fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
          lineHeight: 1.55
        }}>
            Handy Heroes connects Atlanta homeowners with vetted local pros for essential 
            household repairs. Whether your refrigerator is warming or your washer won't spin, 
            we make matching with local technicians straightforward and dependable.
          </p>
        </div>

        <div className="eco-grid mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8">
          {benefits.map((b, i) => <div key={b.title} data-index={i} className="eco-cell flex flex-col items-center text-center">
              <div className="flex items-center justify-center" style={{
            color: "var(--primary)"
          }}>
                {b.icon}
              </div>

              <h3 className="font-serif font-semibold uppercase mt-5" style={{
            color: "var(--foreground)",
            fontSize: "clamp(1.5rem, 2.2vw, 2.125rem)",
            lineHeight: 1.05
          }}>
                {b.title}
              </h3>

              <p className="font-sans mt-4" style={{
            color: "var(--foreground)",
            fontSize: "clamp(1rem, 1vw, 1.0625rem)",
            lineHeight: 1.6
          }}>
                {b.description}
              </p>

              <div className="w-full mt-6" style={{
            height: "1px",
            backgroundColor: "var(--border)"
          }} />

              <p className="font-sans font-medium mt-4 uppercase" style={{
            color: "var(--muted-foreground)",
            fontSize: "clamp(0.8125rem, 0.85vw, 0.875rem)",
            letterSpacing: "0.02em",
            lineHeight: 1.4
          }}>
                {b.caption}
              </p>
            </div>)}
        </div>

        <div className="eco-cta mt-16 flex justify-center">
          <Button asChild className="rounded-lg uppercase font-sans font-semibold px-7 py-4 h-auto bg-primary text-primary-foreground" style={{
          fontSize: "0.875rem",
          letterSpacing: "0.08em"
        }}>
            <Link to="/TrustSafetyPage">View Our Safety Standards</Link>
          </Button>
        </div>
      </div>
    </section>;
}